import { Alert, Descriptions, Divider, Drawer, Empty, Flex, Grid, Skeleton, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../constants/placeholders"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import {
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import { useInvoicePayments } from "../hooks/use-invoice-payments.ts"
import type { InvoiceListItem } from "../types/invoices.types.ts"

const { Text, Title } = Typography

type ViewableInvoice = ProjectInvoice | InvoiceListItem

interface InvoiceViewDrawerProps {
  invoice: ViewableInvoice | null
  onClose: () => void
}

const money = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ""
    ? empty
    : budgetFormat(value)

const balanceOf = (invoice: ViewableInvoice) =>
  (Number(invoice.amount) || 0) - (Number(invoice.paid_to_date) || 0)

/**
 * Every invoice the caller can list, read-only: its header, the lines it
 * bills, the payments against it and what is still owed. Nothing here writes.
 */
export function InvoiceViewDrawer({ invoice, onClose }: InvoiceViewDrawerProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const { timezone, dateFormat } = useDateFormatter()
  const { loading, payments, incomplete } = useInvoicePayments(invoice)

  const statusStyle = invoice
    ? INVOICE_STATUSES_MAP?.[invoice.status as InvoiceStatuses]?.style
    : undefined
  const lines = invoice?.lines ?? []
  const projectLabel =
    (invoice as InvoiceListItem | null)?.project_info?.lead_name ||
    invoice?.project_name

  return (
    <Drawer
      title={t("invoices.view.title", { number: invoice?.invoice_number || "" })}
      placement="right"
      width={screens.md ? 520 : "100%"}
      open={Boolean(invoice)}
      onClose={onClose}
      destroyOnHidden
    >
      {invoice && (
        <Flex vertical gap="middle" data-testid="invoice-view-drawer">
          <Flex justify="space-between" align="center" gap="small" wrap>
            <Title level={5} className="m-0">
              {invoice.invoice_number || empty}
            </Title>
            <Flex gap={4}>
              {invoice.origin_display && (
                <Tag className="m-0">{invoice.origin_display}</Tag>
              )}
              <Tag style={statusStyle} className="m-0">
                {invoice.status_display || empty}
              </Tag>
            </Flex>
          </Flex>

          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label={t("invoices.columns.customer")}>
              {invoice.customer_name || empty}
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.project")}>
              {projectLabel || empty}
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.dateIssued")}>
              {invoice.date_issued || empty}
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.dueDate")}>
              {invoice.due_date || empty}
            </Descriptions.Item>
          </Descriptions>

          <Divider className="my-0" />
          <Text strong>{t("invoices.view.lines")}</Text>
          {lines.length ? (
            <Flex vertical gap={4}>
              {lines.map((line) => (
                <Flex
                  key={line.id ?? line.finance_line}
                  justify="space-between"
                  gap="small"
                  data-testid="invoice-view-line"
                >
                  <Text ellipsis>{line.finance_line_name || empty}</Text>
                  <Text>{money(line.amount)}</Text>
                </Flex>
              ))}
            </Flex>
          ) : (
            <Text type="secondary">{t("invoices.view.noLines")}</Text>
          )}

          <Descriptions column={1} size="small" colon={false}>
            {invoice.subtotal != null && (
              <Descriptions.Item label={t("invoices.view.subtotal")}>
                {money(invoice.subtotal)}
              </Descriptions.Item>
            )}
            {Number(invoice.tax_amount) > 0 && (
              <Descriptions.Item label={t("invoices.view.tax")}>
                {money(invoice.tax_amount)}
              </Descriptions.Item>
            )}
            {Number(invoice.retainage_amount) > 0 && (
              <Descriptions.Item label={t("invoices.view.retainage")}>
                -{money(invoice.retainage_amount)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("invoices.columns.amount")}>
              <Text strong data-testid="invoice-view-amount">
                {money(invoice.amount)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.columns.paidToDate")}>
              <Text data-testid="invoice-view-paid">
                {money(invoice.paid_to_date ?? 0)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t("invoices.view.balance")}>
              <Text strong data-testid="invoice-view-balance">
                {money(balanceOf(invoice))}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider className="my-0" />
          <Text strong>{t("invoices.view.payments")}</Text>
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          ) : (
            <>
              {incomplete && (
                <Alert
                  type="warning"
                  showIcon
                  message={t("invoices.view.paymentsUnavailable")}
                  data-testid="invoice-view-payments-unavailable"
                />
              )}
              {payments.length ? (
                <Flex vertical gap={4}>
                  {payments.map((payment) => (
                    <Flex
                      key={payment.id}
                      justify="space-between"
                      gap="small"
                      data-testid="invoice-view-payment"
                    >
                      <Flex vertical>
                        <Text>
                          {payment.payment_date
                            ? dayjs
                                .utc(payment.payment_date)
                                .tz(timezone)
                                .format(dateFormat)
                            : empty}
                        </Text>
                        <Text type="secondary" className="text-xs">
                          {[payment.payment_method_display, payment.lineName]
                            .filter(Boolean)
                            .join(" · ") || empty}
                        </Text>
                      </Flex>
                      <Text>{money(payment.value)}</Text>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                !incomplete && (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("invoices.view.noPayments")}
                  />
                )
              )}
            </>
          )}
        </Flex>
      )}
    </Drawer>
  )
}
